import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ChevronRight, Sun, Cloud, Zap, Droplets, Leaf,
  CheckCircle2, Phone, Mail, Download
} from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

/* ── 浅色工业风色值 ── */
const ORANGE = 'oklch(0.66 0.19 42)';
const CARD_BG = '#FFFFFF';
const BORDER = '#D0D5E2';
const MUTED_TEXT = '#5A6780';
const DEEP_BG = '#F0F2F5';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

/* ── 技术参数 ── */
const techParams = [
  { test: '拉伸强度', standard: 'ISO 37-2011 / ASTM D412', value: '8 MPa', note: '标准配方实测值' },
  { test: '断裂伸长率', standard: 'ISO 37-2011 / ASTM D412', value: '300%', note: '标准配方实测值' },
  { test: '硬度', standard: 'ISO 48-2010 / ASTM D2240', value: '65±5 Shore A', note: '可定制 40A～80A' },
  { test: '比重', standard: 'ISO 2781-2008', value: '1.25 g/cm³', note: '—' },
  { test: '工作温度范围', standard: '—', value: '-40°C 至 +150°C', note: '可耐短时 +160°C' },
  { test: '压缩永久变形率', standard: 'ISO 815 / ASTM D395', value: '≤25%（参考值）', note: '70°C×22h' },
  { test: '体积电阻率', standard: 'IEC 60093 / ASTM D257', value: '≥10¹³ Ω·cm', note: '绝缘级典型值' },
  { test: '耐臭氧性', standard: 'ISO 1431 / ASTM D1149', value: '无龟裂', note: '50pphm，40°C×72h' },
];

/* ── 核心特性 ── */
const coreFeatures = [
  {
    icon: Sun,
    title: '卓越耐候性',
    titleEn: 'WEATHERING RESISTANCE',
    desc: '在紫外线、氧气、湿气和温度变化等综合大气环境中长期稳定，户外使用寿命远超通用合成橡胶，是建筑与户外装备的理想密封材料。',
  },
  {
    icon: Cloud,
    title: '抗臭氧老化',
    titleEn: 'OZONE RESISTANCE',
    desc: '在高浓度臭氧环境下不易龟裂，保持弹性与密封效能，适合对大气环境有严苛要求的户外设备及输电设施。',
  },
  {
    icon: Zap,
    title: '优异电绝缘性',
    titleEn: 'ELECTRICAL INSULATION',
    desc: '体积电阻率高达 10¹³ Ω·cm 以上，在高压电力设备、变电站、电气柜等场合用作绝缘垫板，安全可靠。',
  },
  {
    icon: Droplets,
    title: '耐水耐蒸汽',
    titleEn: 'WATER & STEAM RESISTANCE',
    desc: '对水、热水及蒸汽具有良好耐受性，体积变化率低，适合水处理、热水管路、饮用水设施中的密封应用，通常符合饮用水接触安全要求。',
  },
  {
    icon: Leaf,
    title: '环保无毒',
    titleEn: 'ECO-FRIENDLY',
    desc: '不含卤素及重金属，符合 ROHS 及主流环保标准，适用于食品机械辅助密封及对环保合规有要求的工程项目。',
  },
];

/* ── 应用场景 ── */
interface AppItem {
  num: string;
  title: string;
  titleEn: string;
  desc: string;
  details: string[];
  img: string | null;
  imgAlt: string;
}

const applications: AppItem[] = [
  {
    num: '①',
    title: '建筑幕墙与门窗密封',
    titleEn: 'FACADE & WINDOW SEALING',
    desc: '现代建筑玻璃幕墙、铝合金门窗及石材外墙拼接处，需要长期抵抗日晒、雨淋、温差及紫外线辐射。EPDM 耐候性突出，在户外环境中能稳定保持弹性与密封，不泛白、不龟裂，是幕墙工程中最常用的密封橡胶材料之一。',
    details: ['幕墙玻璃压条', '铝合金门窗密封条', '外墙变形缝嵌条', '玻璃结构胶配合衬垫'],
    img: IMAGES.P09_CURTAIN_WALL_1_208,
    imgAlt: '建筑幕墙EPDM密封应用',
  },
  {
    num: '②',
    title: '屋面防水系统',
    titleEn: 'ROOFING WATERPROOFING',
    desc: 'EPDM 橡胶卷材和板材广泛用于平屋面、斜屋面及种植屋面的防水层，其耐候性与弹性伸缩性能在温差极端的气候下仍可保持防水效果，工程应用中寿命可达数十年。典型用途包括防水铺装层、屋面排水收边压条及穿孔部位柔性封堵。',
    details: ['平屋面防水铺装层', '排水口周边封堵垫', '穿屋面管道柔性密封', '女儿墙压顶收边'],
    img: IMAGES.P09_EPDM_ROOFTOP_1_174,
    imgAlt: 'EPDM屋面防水系统应用',
  },
  {
    num: '③',
    title: '电力设施绝缘防护',
    titleEn: 'ELECTRICAL INSULATION',
    desc: '在变电站、配电柜、户外高压设备及电气工程的绝缘防护中，EPDM 凭借高体积电阻率、优良耐候性及对水汽的稳定性，被用作绝缘垫板、设备防护衬垫和户外配电柜密封条，兼顾绝缘安全与户外长效稳定。',
    details: ['变电站设备绝缘垫板', '配电柜防护密封条', '户外高压设备防护垫', '电缆进线口封堵圈'],
    img: IMAGES.P11_SUBSTATION_1_191,
    imgAlt: '变电站EPDM绝缘防护应用',
  },
  {
    num: '④',
    title: '汽车与轨道交通密封',
    titleEn: 'AUTOMOTIVE & RAIL SEALING',
    desc: '汽车车身、天窗、车门及发动机舱非接触燃油部位的密封，以及轨道车辆门窗边框的密封条，要求长期耐候、抗紫外线且振动条件下不脱落。EPDM 在非油性介质和大气环境下的综合密封表现优秀，是汽车外部密封件的主流材料。',
    details: ['车门/车窗密封条', '天窗周边密封', '发动机舱非油区密封', '轨道车辆门窗框密封'],
    img: IMAGES.P08_EPDM_AUTOMOTIVE_1_1_204,
    imgAlt: 'EPDM汽车密封条应用',
  },
  {
    num: '⑤',
    title: '水处理与饮用水设施',
    titleEn: 'WATER TREATMENT',
    desc: '污水处理厂、净水站、游泳池及饮用水管道系统中，需要与水、稀酸、稀碱长期接触的密封部件。EPDM 对水介质具有优良耐受性，且常规配方无卤素及重金属，适合在饮用水设施中应用（具体配方需符合当地饮用水接触标准）。',
    details: ['净水站管道密封垫', '水处理容器衬垫', '泵体密封件', '饮用水管道接口垫片'],
    img: IMAGES.P09_EPDM_EXTRA2_1_193,
    imgAlt: 'EPDM水处理设施应用',
  },
];

/* ── 定制能力对比 ── */
const customFormulas = [
  { dim: '工作温度上限', standard: '+150°C', custom: '可评估至 +160°C（短时）', use: '高温蒸汽管路周边密封' },
  { dim: '工作温度下限', standard: '-40°C', custom: '可评估至 -50°C', use: '寒区户外设备' },
  { dim: '硬度', standard: '65±5 Shore A', custom: '可向 40A 或 80A 调配', use: '软密封面或高刚度支撑' },
  { dim: '颜色', standard: '黑色 / 灰色', custom: '可协调彩色配方（红/蓝/绿等）', use: '有色彩识别要求的场合' },
  { dim: '绝缘等级', standard: '通用绝缘配方', custom: '可强化为高压绝缘专用配方', use: '电力高压设备专用' },
  { dim: '厚度 / 幅宽', standard: '1.5 ~ 20mm / 标准幅宽', custom: '可定制', use: '异形大尺寸件、特厚板' },
];

/* ── 产品规格 ── */
const specList = [
  { label: '颜色规格', value: '黑色 / 灰色 / 彩色（定制）' },
  { label: '厚度范围', value: '1.5mm 至 20.0mm' },
  { label: '幅宽', value: '0.91m 至 2m（标准），可定制' },
  { label: '硬度区间', value: '40A ～ 80A Shore A（可定制）' },
  { label: '定制服务', value: '支持裁切、定制尺寸及特殊配方' },
];

export default function ProductEPDM() {
  return (
    <div className="bg-background text-foreground min-h-screen">

      {/* ── 面包屑导航 ── */}
      <div className="border-b border-border" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-xs" style={{ color: MUTED_TEXT }}>
            <Link to="/" className="hover:text-primary transition-colors">首页</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={ROUTE_PATHS.PRODUCTS} className="hover:text-primary transition-colors">产品系列</Link>
            <ChevronRight className="w-3 h-3" />
            <span style={{ color: ORANGE }}>EPDM 三元乙丙橡胶板</span>
          </div>
        </div>
      </div>

      {/* ── Hero 区 ── */}
      <section className="relative overflow-hidden" style={{ background: DEEP_BG, minHeight: 420 }}>
        <img
          src={IMAGES.P09_EPDM_ROOFTOP_1_174}
          alt="EPDM屋面防水应用"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.12 }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(15,30,53,0.90) 0%, rgba(15,30,53,0.65) 100%)' }}
        />
        <div className="relative container mx-auto px-4 py-16 lg:py-24">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            {/* 返回 */}
            <motion.div variants={fadeUp} className="mb-8">
              <Link
                to={ROUTE_PATHS.PRODUCTS}
                className="inline-flex items-center gap-2 text-sm transition-colors"
                style={{ color: MUTED_TEXT }}
              >
                <ArrowLeft className="w-4 h-4" />
                返回产品列表
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* 左：文字信息 */}
              <div>
                <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
                  <span
                    className="px-3 py-1 text-xs font-black tracking-[0.2em] uppercase rounded-sm"
                    style={{ background: 'oklch(0.66 0.19 42 / 0.15)', color: ORANGE, border: `1px solid ${ORANGE}` }}
                  >
                    EPDM
                  </span>
                  <span className="text-xs tracking-widest uppercase" style={{ color: MUTED_TEXT }}>
                    ETHYLENE PROPYLENE DIENE MONOMER
                  </span>
                </motion.div>

                <motion.h1 variants={fadeUp} className="text-4xl lg:text-5xl font-black mb-4 leading-tight">
                  <span style={{ color: ORANGE }}>EPDM</span> 三元乙丙橡胶板
                </motion.h1>
                <motion.p variants={fadeUp} className="text-lg font-medium mb-2" style={{ color: 'oklch(0.75 0.010 225)' }}>
                  户外建筑与电力设施的长效密封首选
                </motion.p>
                <motion.p variants={fadeUp} className="text-sm leading-relaxed mb-8" style={{ color: MUTED_TEXT }}>
                  EPDM 以卓越的耐候性、抗臭氧老化及优异的电气绝缘性能，广泛应用于建筑幕墙、屋面防水、
                  电力绝缘及水处理设施。所有数据均符合 ISO / ASTM 国际标准体系。
                </motion.p>

                <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                  <Link
                    to={ROUTE_PATHS.CONTACT}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm"
                    style={{ background: ORANGE, color: '#FFFFFF' }}
                  >
                    <Phone className="w-4 h-4" />
                    立即询价
                  </Link>
                  <Link
                    to={`${ROUTE_PATHS.TECH_SPECS}#epdm`}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm"
                    style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
                  >
                    查看技术参数
                  </Link>
                </motion.div>
              </div>

              {/* 右：产品图 + 快速规格 */}
              <motion.div variants={fadeUp} className="flex flex-col gap-4">
                <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                  <img
                    src={IMAGES.P09_EPDM_SHEET_1_170}
                    alt="EPDM三元乙丙橡胶板材实物"
                    className="w-full object-cover"
                    style={{ maxHeight: 260, objectPosition: 'center' }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: '工作温度', value: '-40°C ~ +150°C', sub: '可耐短时 +160°C' },
                    { label: '标准硬度', value: '65±5 Shore A', sub: '可定制 40A～80A' },
                    { label: '板材厚度', value: '1.5 ~ 20.0mm', sub: '幅宽 0.91m ～ 2m' },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="rounded p-3 text-center"
                      style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                    >
                      <p className="text-[10px] tracking-widest uppercase mb-1" style={{ color: MUTED_TEXT }}>{s.label}</p>
                      <p className="text-sm font-bold leading-tight" style={{ color: ORANGE }}>{s.value}</p>
                      <p className="text-[10px] mt-0.5" style={{ color: MUTED_TEXT }}>{s.sub}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 核心特性 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CORE PROPERTIES</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">核心特性</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {coreFeatures.map((f) => {
                const Icon = f.icon;
                return (
                  <motion.div
                    key={f.title}
                    variants={fadeUp}
                    className="rounded p-5 flex flex-col gap-3"
                    style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                  >
                    <div
                      className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0"
                      style={{ background: 'oklch(0.66 0.19 42 / 0.12)', border: '1px solid oklch(0.66 0.19 42 / 0.3)' }}
                    >
                      <Icon className="w-5 h-5" style={{ color: ORANGE }} />
                    </div>
                    <div>
                      <p className="text-[9px] font-bold tracking-widest uppercase mb-0.5" style={{ color: MUTED_TEXT }}>{f.titleEn}</p>
                      <p className="text-sm font-bold mb-2">{f.title}</p>
                      <p className="text-xs leading-relaxed" style={{ color: MUTED_TEXT }}>{f.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 技术参数 ── */}
      <section id="tech-params" className="py-16 lg:py-20" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>TECHNICAL DATA</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">EPDM 实测物理性能数据</h2>
              <p className="text-sm text-center mt-2" style={{ color: MUTED_TEXT }}>
                所有数值均为标准配方典型实测值，依据 ISO / ASTM 国际标准体系测试
              </p>
            </motion.div>

            {/* 四大核心指标 */}
            <motion.div variants={fadeUp} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: '拉伸强度', value: '8 MPa', sub: 'ISO 37 / ASTM D412' },
                { label: '断裂伸长率', value: '300%', sub: 'ISO 37 / ASTM D412' },
                { label: '体积电阻率', value: '≥10¹³ Ω·cm', sub: 'IEC 60093 / ASTM D257' },
                { label: '最低工作温度', value: '-40°C', sub: '优于同类通用橡胶' },
              ].map((m) => (
                <div
                  key={m.label}
                  className="rounded p-5 text-center"
                  style={{ background: CARD_BG, border: '1px solid oklch(0.66 0.19 42 / 0.25)' }}
                >
                  <p className="text-3xl font-black mb-1" style={{ color: ORANGE }}>{m.value}</p>
                  <p className="text-sm font-semibold mb-1">{m.label}</p>
                  <p className="text-[10px] font-mono" style={{ color: MUTED_TEXT }}>{m.sub}</p>
                </div>
              ))}
            </motion.div>

            {/* 完整参数表 */}
            <motion.div variants={fadeUp} className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                    {['测试项目', '测试标准', '典型数值', '参考说明'].map((h) => (
                      <th
                        key={h}
                        className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase"
                        style={{ color: ORANGE }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {techParams.map((row, i) => (
                    <tr
                      key={row.test}
                      style={{
                        background: i % 2 === 0 ? CARD_BG : '#F0F2F5',
                        borderBottom: `1px solid ${BORDER}`,
                      }}
                    >
                      <td className="px-5 py-3.5 font-semibold">{row.test}</td>
                      <td className="px-5 py-3.5 font-mono text-xs" style={{ color: MUTED_TEXT }}>{row.standard}</td>
                      <td className="px-5 py-3.5 font-bold" style={{ color: ORANGE }}>{row.value}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-5 flex items-start gap-2 text-xs" style={{ color: MUTED_TEXT }}>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ORANGE }} />
              <span>
                适用国际测试体系：ISO（国际标准化组织）、ASTM（美国材料与试验协会）、IEC（国际电工委员会）。
                依据客户要求可提供对应检测标准的测试数据，满足进口商合规要求。具体项目请索取完整 TDS 技术数据表。
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 典型应用场景 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-12">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>EPDM APPLICATIONS</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">经过验证的主要应用领域</h2>
            </motion.div>

            <div className="space-y-8">
              {applications.map((app, idx) => (
                <motion.div
                  key={app.num}
                  variants={fadeUp}
                  className="rounded overflow-hidden"
                  style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                >
                  <div className={`grid grid-cols-1 lg:grid-cols-2 ${idx % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                    {/* 图片列 */}
                    <div
                      className={`relative overflow-hidden ${idx % 2 === 1 ? 'lg:order-2' : ''}`}
                      style={{ minHeight: 260 }}
                    >
                      {app.img ? (
                        <img
                          src={app.img}
                          alt={app.imgAlt}
                          className="absolute inset-0 w-full h-full object-cover"
                          style={{ opacity: 0.88 }}
                        />
                      ) : (
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center gap-3"
                          style={{ background: '#ECEEF2', border: `2px dashed ${BORDER}` }}
                        >
                          <p className="text-xs font-mono text-center px-6 leading-relaxed" style={{ color: MUTED_TEXT }}>
                            图片位置保留
                          </p>
                        </div>
                      )}
                      <div
                        className="absolute top-4 left-4 w-10 h-10 rounded-sm flex items-center justify-center font-black text-lg z-10"
                        style={{ background: ORANGE, color: '#FFFFFF' }}
                      >
                        {app.num}
                      </div>
                    </div>

                    {/* 内容列 */}
                    <div className={`p-8 flex flex-col justify-center ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <p className="text-[10px] font-bold tracking-widest uppercase mb-2" style={{ color: ORANGE }}>{app.titleEn}</p>
                      <h3 className="text-xl font-black mb-4">{app.title}</h3>
                      <p className="text-sm leading-relaxed mb-6" style={{ color: MUTED_TEXT }}>{app.desc}</p>
                      <div>
                        <p className="text-xs font-bold tracking-wider uppercase mb-3" style={{ color: MUTED_TEXT }}>典型用途</p>
                        <div className="grid grid-cols-2 gap-2">
                          {app.details.map((d) => (
                            <div key={d} className="flex items-center gap-2 text-xs">
                              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
                              {d}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── EPDM vs 其他材料对比 ── */}
      <section className="py-16 lg:py-20" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>MATERIAL COMPARISON</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">EPDM 与同类材料性能对比</h2>
              <p className="text-sm text-center mt-2 max-w-2xl mx-auto" style={{ color: MUTED_TEXT }}>
                在耐候性与绝缘性方面，EPDM 具有明显优势；但对于石油基油脂、强溶剂接触场合，应选用 NBR 或 FKM。
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                    {['性能维度', 'EPDM', 'NBR 丁腈', 'FKM 氟橡胶', 'NR 天然橡胶'].map((h, i) => (
                      <th
                        key={h}
                        className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase"
                        style={{ color: i === 1 ? ORANGE : 'oklch(0.70 0.015 225)' }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { dim: '耐候/抗臭氧', epdm: '★★★★★', nbr: '★★☆☆☆', fkm: '★★★★☆', nr: '★★☆☆☆' },
                    { dim: '电气绝缘', epdm: '★★★★★', nbr: '★★★☆☆', fkm: '★★★☆☆', nr: '★★★★☆' },
                    { dim: '耐高温（150°C+）', epdm: '★★★☆☆', nbr: '★★☆☆☆', fkm: '★★★★★', nr: '★★☆☆☆' },
                    { dim: '耐石油基油脂', epdm: '★☆☆☆☆', nbr: '★★★★★', fkm: '★★★★★', nr: '★☆☆☆☆' },
                    { dim: '耐水/蒸汽', epdm: '★★★★★', nbr: '★★★☆☆', fkm: '★★★☆☆', nr: '★★★☆☆' },
                    { dim: '综合成本', epdm: '★★★★☆', nbr: '★★★★★', fkm: '★★☆☆☆', nr: '★★★★☆' },
                  ].map((row, i) => (
                    <tr key={row.dim} style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}>
                      <td className="px-5 py-3.5 font-semibold">{row.dim}</td>
                      <td className="px-5 py-3.5 font-bold" style={{ color: ORANGE }}>{row.epdm}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.nbr}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.fkm}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.nr}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
            <motion.div variants={fadeUp} className="mt-4 flex items-start gap-2 text-xs" style={{ color: MUTED_TEXT }}>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ORANGE }} />
              <span>★ 评级仅为综合性能横向对比参考，实际应用请结合具体工况数据进行选型评估。如需专业选型指导，请联系我们的技术团队。</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 定制配方 + 规格 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CUSTOM FORMULATION & SPECS</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">定制配方能力与产品规格</h2>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* 定制能力表 */}
              <motion.div variants={fadeUp}>
                <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: MUTED_TEXT }}>定制调整维度</h3>
                <div className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                        {['调整维度', '标准值', '可调方向'].map((h) => (
                          <th key={h} className="px-4 py-3 text-left font-black tracking-widest uppercase" style={{ color: ORANGE }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {customFormulas.map((row, i) => (
                        <tr key={row.dim} style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}>
                          <td className="px-4 py-3 font-semibold">{row.dim}</td>
                          <td className="px-4 py-3 font-mono" style={{ color: 'oklch(0.78 0.012 225)' }}>{row.standard}</td>
                          <td className="px-4 py-3 font-bold" style={{ color: ORANGE }}>{row.custom}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {/* 产品规格 + 板材图 */}
              <motion.div variants={fadeUp} className="flex flex-col gap-6">
                <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                  <img
                    src={IMAGES.P09_EPDM_BOARD_1_179}
                    alt="EPDM橡胶板材规格"
                    className="w-full object-cover"
                    style={{ maxHeight: 200 }}
                  />
                </div>
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: MUTED_TEXT }}>标准产品规格</h3>
                  <div className="space-y-2">
                    {specList.map((spec) => (
                      <div
                        key={spec.label}
                        className="flex items-center gap-4 text-sm py-2.5"
                        style={{ borderBottom: `1px solid ${BORDER}` }}
                      >
                        <span className="w-28 flex-shrink-0 text-xs font-bold tracking-wider uppercase" style={{ color: MUTED_TEXT }}>
                          {spec.label}
                        </span>
                        <span className="font-semibold">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 绝缘图+窗密封图展示 ── */}
      <section className="py-12" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <motion.div variants={fadeUp} className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
              <img
                src={IMAGES.P09_EPDM_INSULATION_1_180}
                alt="EPDM绝缘应用场景"
                className="w-full object-cover"
                style={{ maxHeight: 220 }}
              />
              <div className="p-4" style={{ background: CARD_BG }}>
                <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: ORANGE }}>ELECTRICAL INSULATION</p>
                <p className="text-sm font-semibold">EPDM 绝缘防护应用</p>
                <p className="text-xs mt-1" style={{ color: MUTED_TEXT }}>
                  体积电阻率 ≥10¹³ Ω·cm，适用于高压电力设施绝缘垫板及户外电气设备防护。
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
              <img
                src={IMAGES.P11_WINDOW_SEAL_1_196}
                alt="EPDM门窗密封条"
                className="w-full object-cover"
                style={{ maxHeight: 220 }}
              />
              <div className="p-4" style={{ background: CARD_BG }}>
                <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: ORANGE }}>WINDOW & DOOR SEALING</p>
                <p className="text-sm font-semibold">门窗密封条应用</p>
                <p className="text-xs mt-1" style={{ color: MUTED_TEXT }}>
                  EPDM 密封条耐候、耐臭氧、压缩回弹好，广泛用于建筑铝合金门窗及幕墙玻璃压条。
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-14" style={{ background: '#F0F2F5', borderTop: `1px solid ${BORDER}` }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.div variants={fadeUp} className="mb-4">
              <span className="text-xs font-bold tracking-widest uppercase" style={{ color: ORANGE }}>GET A QUOTE</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-2xl lg:text-3xl font-black mb-4">
              <>需要 EPDM 三元乙丙橡胶板？<br />我们提供技术选型与样品支持</>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-sm mb-8" style={{ color: MUTED_TEXT }}>
              说明您的工况（耐候等级、介质接触、规格尺寸），技术顾问将为您提供选型建议并安排样品。
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to={ROUTE_PATHS.CONTACT}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-black rounded-sm"
                style={{ background: ORANGE, color: '#FFFFFF' }}
              >
                <Mail className="w-4 h-4" />
                提交询价
              </Link>
              <a
                href="tel:13426031477"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm"
                style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
              >
                <Phone className="w-4 h-4" />
                13426031477
              </a>
              <Link
                to={ROUTE_PATHS.SELECTION}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm"
                style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
              >
                <Download className="w-4 h-4" />
                查看选型矩阵
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
